/**
 * AdminCustomerManager.js
 * Admin panel page to manage customer accounts for the wedding dashboard
 * Features: Create, view, edit customer accounts and linked weddings
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  Plus,
  Eye,
  Edit,
  Trash2,
  Key,
  Loader2,
  CheckCircle,
  XCircle,
  Copy,
  Calendar,
  Mail,
  Phone,
  Heart
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

const AdminCustomerManager = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [creating, setCreating] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    customer_name: "",
    partner_name: "",
    phone: "",
    wedding_date: "",
    venue_name: "KAI by Happy Retreats",
    package_type: "Standard",
    notes: "",
    modules_enabled: ["food_menu", "room_allocation", "itinerary", "invite_page", "decor"]
  });

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/admin/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers || []);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Generate random password
  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let password = "";
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  // Create customer
  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/accounts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`Customer account created for ${formData.customer_name}`);
        
        // Show credentials
        toast.info(
          <div className="space-y-2">
            <p className="font-medium">Login Credentials</p>
            <p className="text-sm">Email: {formData.email}</p>
            <p className="text-sm">Password: {formData.password}</p>
            <p className="text-xs text-gray-500">Share these securely with the customer</p>
          </div>,
          { duration: 10000 }
        );

        setIsCreateDialogOpen(false);
        resetForm();
        fetchCustomers();
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to create customer";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      customer_name: "",
      partner_name: "",
      phone: "",
      wedding_date: "",
      venue_name: "KAI by Happy Retreats",
      package_type: "Standard",
      notes: "",
      modules_enabled: ["food_menu", "room_allocation", "itinerary", "invite_page", "decor"]
    });
  };

  // View customer details
  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setIsViewDialogOpen(true);
  };

  // Reset password
  const handleResetPassword = async (customerId) => {
    if (!window.confirm("Generate a new password for this customer?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.post(`${API}/admin/accounts/${customerId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(
          <div className="space-y-2">
            <p className="font-medium">New Password Generated</p>
            <p className="text-sm font-mono bg-gray-100 p-2 rounded">{response.data.new_password}</p>
            <p className="text-xs text-gray-500">Share this securely with the customer</p>
          </div>,
          { duration: 15000 }
        );
      }
    } catch (error) {
      toast.error("Failed to reset password");
    }
  };

  // Deactivate customer
  const handleDeactivate = async (customerId, customerName) => {
    if (!window.confirm(`Deactivate account for ${customerName}? They will no longer be able to login.`)) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/accounts/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Customer account deactivated");
      fetchCustomers();
    } catch (error) {
      toast.error("Failed to deactivate account");
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Module toggle
  const toggleModule = (module) => {
    setFormData(prev => ({
      ...prev,
      modules_enabled: prev.modules_enabled.includes(module)
        ? prev.modules_enabled.filter(m => m !== module)
        : [...prev.modules_enabled, module]
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#B08968]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#B08968]" />
              Customer Dashboard Manager
            </h1>
            <p className="text-gray-600 mt-1">
              Manage customer accounts for the wedding planning dashboard
            </p>
          </div>

          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-[#B08968] hover:bg-[#9A7558]">
                <Plus className="w-4 h-4" />
                Create Customer Account
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#B08968]" />
                  Create New Customer Account
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleCreateCustomer} className="space-y-6 mt-4">
                {/* Couple Details */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 border-b pb-2">Couple Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Customer Name *</Label>
                      <Input
                        value={formData.customer_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                        placeholder="Primary contact name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Partner Name</Label>
                      <Input
                        value={formData.partner_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, partner_name: e.target.value }))}
                        placeholder="Partner's name"
                      />
                    </div>
                  </div>
                </div>

                {/* Login Credentials */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 border-b pb-2">Login Credentials</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="customer@email.com"
                        required
                      />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Enter password"
                          required
                        />
                        <Button type="button" variant="outline" onClick={generatePassword}>
                          Generate
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                {/* Wedding Details */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 border-b pb-2">Wedding Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Wedding Date</Label>
                      <Input
                        type="date"
                        value={formData.wedding_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, wedding_date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Venue</Label>
                      <Input
                        value={formData.venue_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Package Type</Label>
                    <select
                      value={formData.package_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, package_type: e.target.value }))}
                      className="w-full h-10 px-3 border rounded-md"
                    >
                      <option value="Standard">Standard</option>
                      <option value="Premium">Premium</option>
                      <option value="Luxury">Luxury</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Any special notes about this booking..."
                      rows={2}
                    />
                  </div>
                </div>

                {/* Modules Access */}
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-700 border-b pb-2">Enabled Modules</h3>
                  <div className="flex flex-wrap gap-2">
                    {["food_menu", "room_allocation", "itinerary", "invite_page", "decor"].map(module => (
                      <button
                        key={module}
                        type="button"
                        onClick={() => toggleModule(module)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          formData.modules_enabled.includes(module)
                            ? "bg-[#B08968] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {module.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={creating} className="bg-[#B08968] hover:bg-[#9A7558]">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Total Customers</p>
            <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Active Accounts</p>
            <p className="text-2xl font-bold text-green-600">
              {customers.filter(c => c.is_active !== false).length}
            </p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">This Month</p>
            <p className="text-2xl font-bold text-blue-600">
              {customers.filter(c => {
                const created = new Date(c.created_at);
                const now = new Date();
                return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
              }).length}
            </p>
          </div>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Couple</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Wedding Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No customer accounts yet. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{customer.customer_name}</p>
                        {customer.partner_name && (
                          <p className="text-sm text-gray-500">& {customer.partner_name}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-sm">{customer.email}</span>
                        <button
                          onClick={() => copyToClipboard(customer.email)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <Copy className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.wedding?.wedding_date || "-"}
                    </TableCell>
                    <TableCell>
                      {customer.is_active !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                          <XCircle className="w-3 h-3" />
                          Inactive
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(customer.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewCustomer(customer)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(customer.id)}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivate(customer.id, customer.customer_name)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* View Customer Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Customer Details</DialogTitle>
            </DialogHeader>
            {selectedCustomer && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Customer Name</p>
                    <p className="font-medium">{selectedCustomer.customer_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Partner Name</p>
                    <p className="font-medium">{selectedCustomer.partner_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{selectedCustomer.phone || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Wedding Date</p>
                    <p className="font-medium">{selectedCustomer.wedding?.wedding_date || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Venue</p>
                    <p className="font-medium">{selectedCustomer.wedding?.venue_name || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Package</p>
                    <p className="font-medium">{selectedCustomer.wedding?.package_type || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Last Login</p>
                    <p className="font-medium">
                      {selectedCustomer.last_login 
                        ? new Date(selectedCustomer.last_login).toLocaleString() 
                        : "Never"}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-2">Enabled Modules</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomer.modules_enabled?.map(module => (
                      <span key={module} className="px-2 py-1 bg-[#B08968]/10 text-[#8B6914] rounded text-xs">
                        {module.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </div>
                {selectedCustomer.wedding?.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Notes</p>
                    <p className="text-sm">{selectedCustomer.wedding.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCustomerManager;
